// Prévia ilustrativa do prazo de entrega (H6, PO1-3264). Função pura, sem efeito no
// cálculo real (que só chega com o PO1-2886).
//
// Regras simuladas:
//   - emissão DEPOIS da hora de corte → a contagem começa no dia seguinte;
//   - prazo = dias da tabela + dias adicionais (PrazoEntregaDiasAcumular);
//   - DC conta dias corridos; DU pula sábado e domingo; DU_S pula só domingo;
//   - feriados nacionais fixos também são pulados em DU/DU_S quando
//     `consideraFeriado` (PrazoEntregaConsiderarFeriadoOrigem/Destino) — padrão SIM,
//     que é como o caso de referência do mockup fecha (12/10 é feriado);
//   - `contarDiaEmissao`: o dia de início conta como dia 1 (se for dia contável).
//
// Devolve { dataPrevista: Date, iso: 'YYYY-MM-DD', inicio: Date, aposCorte, totalDias,
//           diasPulados } ou null quando a entrada não dá para interpretar.
export const TIPOS_DIAS = ["DU", "DU_S", "DC"];

// Feriados nacionais de data fixa (os móveis — Carnaval, Sexta-feira Santa, Corpus
// Christi — ficam de fora da prévia, de propósito: é ilustração, não cálculo).
export const FERIADOS_NACIONAIS_FIXOS = ["01-01", "04-21", "05-01", "09-07", "10-12", "11-02", "11-15", "12-25"];

function pad(n) {
  return n < 10 ? `0${n}` : String(n);
}

export function isoLocal(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Aceita Date, 'YYYY-MM-DDTHH:mm', 'YYYY-MM-DD HH:mm' ou 'YYYY-MM-DD' (hora 00:00),
// sempre no horário local, sem passar pelo parser de ISO (que trata como UTC).
export function parseDataHora(entrada) {
  if (entrada instanceof Date) return Number.isNaN(entrada.getTime()) ? null : entrada;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(String(entrada || "").trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0), 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

function minutosDe(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || "").trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function inteiro(v, padrao) {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n >= 0 ? n : padrao;
}

function avancar(d, n) {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  r.setDate(r.getDate() + n);
  return r;
}

function ehFeriado(d, feriados) {
  const mmdd = `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const iso = isoLocal(d);
  return feriados.some((f) => f === mmdd || f === iso);
}

export function diaConta(d, tipoDias, feriados) {
  if (tipoDias === "DC") return true;
  const dow = d.getDay();
  if (dow === 0) return false;
  if (tipoDias === "DU" && dow === 6) return false;
  return !(feriados && feriados.length && ehFeriado(d, feriados));
}

export function calcularPreviaPrazo({
  dataEmissao,
  prazoTabelaDias,
  diasAdicionais,
  tipoDias,
  horaCorte,
  contarDiaEmissao,
  consideraFeriado,
  feriados,
}) {
  const emissao = parseDataHora(dataEmissao);
  if (!emissao) return null;
  const tipo = TIPOS_DIAS.indexOf(tipoDias) !== -1 ? tipoDias : "DU";
  const total = inteiro(prazoTabelaDias, 0) + inteiro(diasAdicionais, 0);
  const listaFeriados =
    consideraFeriado === false ? [] : Array.isArray(feriados) ? feriados : FERIADOS_NACIONAIS_FIXOS;

  const corte = minutosDe(horaCorte);
  const minutoEmissao = emissao.getHours() * 60 + emissao.getMinutes();
  const aposCorte = corte !== null && minutoEmissao > corte;

  let dia = avancar(emissao, aposCorte ? 1 : 0);
  const inicio = dia;
  let contados = 0;
  let pulados = 0;

  if (total > 0) {
    if (contarDiaEmissao && diaConta(dia, tipo, listaFeriados)) contados = 1;
    while (contados < total) {
      dia = avancar(dia, 1);
      if (diaConta(dia, tipo, listaFeriados)) contados += 1;
      else pulados += 1;
    }
  }

  return {
    dataPrevista: dia,
    iso: isoLocal(dia),
    inicio,
    aposCorte,
    totalDias: total,
    diasPulados: pulados,
    tipoDias: tipo,
  };
}
